import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'wrapper',
    'filter',
    'link',
    'indicator',
    'all',
    'dynamic',
    'lz',
    'fj',
    'fm',
    'element',
    'hiddenStem',
    'mentalPattern',
    'mentalPatternHiddenStem',
    'qiPhase',
    'qiPhaseHiddenStem',
    'star',
    'iGing',
    'naYin',
    'bazi',
  ];

  connect() {
    if (!this.aspectsController)
      console.error("Can't find aspects controller!");

    if (this.hasFilterTarget) {
      this.loadFilter();
    }

    this.update();
  }

  update(event) {
    this.checkDependingFilters(event, 'elements', ['hidden_stems'], {
      uncheckParentWhenSubIsUnchecked: false,
      checkParentWhenAllSubAreChecked: true,
    });
    this.checkDependingFilters(event, 'dynamics', ['lz', 'fj', 'fm']);
    this.checkDependingFilters(event, 'aspects', [
      'festigend',
      'loesend',
      'weitere',
    ]);
    this.checkDependingFilters(event, 'all', [
      'dynamics',
      'lz',
      'fj',
      'fm',
      'elements',
      'hidden_stems',
      'mental_patterns',
      'aspects',
      'festigend',
      'loesend',
      'weitere',
      'qi_phases',
      'stars',
      'i_ging',
      'na_yin',
      'bazi',
      'wiki',
    ]);

    const currentFilter = this.currentFilter();

    if (this.hasIndicatorTarget) {
      if (this.isDefaultFilter(currentFilter)) {
        this.indicatorTarget.classList.toggle('text-gold', false);
        this.indicatorTarget.classList.toggle('border-gold', false);
        this.indicatorTarget.classList.toggle('text-white', true);
        this.indicatorTarget.classList.toggle('border-white', true);
      } else {
        this.indicatorTarget.classList.toggle('text-gold', true);
        this.indicatorTarget.classList.toggle('text-white', false);
        this.indicatorTarget.classList.toggle('border-gold', true);
        this.indicatorTarget.classList.toggle('border-white', false);
      }
    }

    for (const [filterName, targets] of Object.entries({
      dynamics: this.dynamicTargets,
      lz: this.lzTargets,
      fj: this.fjTargets,
      fm: this.fmTargets,
      mental_patterns: this.mentalPatternTargets,
      elements: this.elementTargets,
      hidden_stems: this.hiddenStemTargets,
      qi_phases: this.qiPhaseTargets,
      stars: this.starTargets,
      i_ging: this.iGingTargets,
      na_yin: this.naYinTargets,
      bazi: this.baziTargets,
    }))
      targets.forEach((target) =>
        target.classList.toggle(
          'invisible',
          !currentFilter.includes(filterName),
        ),
      );

    this.mentalPatternHiddenStemTargets.forEach((target) =>
      target.classList.toggle(
        'invisible',
        !currentFilter.includes('mental_patterns') ||
          (currentFilter.includes('elements') &&
            !currentFilter.includes('hidden_stems')),
      ),
    );

    this.qiPhaseHiddenStemTargets.forEach((target) =>
      target.classList.toggle(
        'invisible',
        !currentFilter.includes('qi_phases') ||
          (currentFilter.includes('elements') &&
            !currentFilter.includes('hidden_stems')),
      ),
    );

    document.querySelectorAll('.wiki').forEach((element) => {
      ['pointer-events-none', 'no-underline'].forEach((v) =>
        element.classList.toggle(v, !currentFilter.includes('wiki')),
      );
    });

    document.querySelectorAll('.internal-wiki').forEach((element) => {
      ['pointer-events-none', 'no-underline'].forEach((v) =>
        element.classList.toggle(v, !currentFilter.includes('wiki')),
      );
    });

    // Update URL in PDF download button
    if (this.hasLinkTarget) {
      const url = new URL(this.linkTarget.dataset.href);
      const params = new URLSearchParams(url.search);

      this.filterTargets.forEach((filterTarget) => {
        if (filterTarget.checked) {
          params.set(`display_${filterTarget.dataset.filter}`, 'true');
        } else {
          params.set(`display_${filterTarget.dataset.filter}`, 'false');
        }
      });

      let hash = document.location.hash.substring(1);
      if (hash) hash = `#${hash}`;

      // Write url to data-href, so it's accessible by `pdf-download` controller
      this.linkTarget.dataset.href =
        url.origin + url.pathname + '?' + params.toString() + hash;
    }

    this.aspectsController.update();

    if (event) {
      this.saveFilter(currentFilter);
    }
  }

  checkDependingFilters(
    event,
    system,
    subsystems,
    options = {
      checkParentWhenAllSubAreChecked: true,
      uncheckParentWhenSubIsUnchecked: true,
    },
  ) {
    const currentFilter = this.currentFilter();

    // When parent filter is unchecked, uncheck all sub filters
    if (
      event?.target.dataset.filter === system &&
      !currentFilter.includes(system)
    ) {
      this.filterTargets
        .filter((element) => subsystems.includes(element.dataset.filter))
        .forEach((element) => (element.checked = false));
    }

    // When parent filter is checked, check all sub filters
    else if (
      event?.target.dataset.filter === system &&
      currentFilter.includes(system)
    ) {
      this.filterTargets
        .filter((element) => subsystems.includes(element.dataset.filter))
        .forEach((element) => (element.checked = !element.disabled));
    }

    // when sub filter is unchecked, uncheck the parent filter (but only if this is wanted)
    else if (
      options.uncheckParentWhenSubIsUnchecked &&
      event?.target.dataset.filter &&
      subsystems.includes(event.target.dataset.filter) &&
      !currentFilter.includes(event.target.dataset.filter)
    ) {
      this.filterTargets
        .filter((element) => element.dataset.filter === system)
        .forEach((element) => (element.checked = false));
    }

    // When all sub filters are checked, check the parent filter (but only if this is wanted)
    else if (
      options.checkParentWhenAllSubAreChecked &&
      currentFilter.filter((filter) => subsystems.includes(filter)).length ===
        subsystems.length
    ) {
      this.filterTargets
        .filter((element) => element.dataset.filter === system)
        .forEach((element) => (element.checked = !element.disabled));
    }
  }

  currentFilter() {
    if (!this.hasFilterTarget)
      // No filter button present, so use default filter
      return this.defaultFilter;

    return this.filterTargets
      .filter((target) => target.checked)
      .map((target) => target.dataset.filter);
  }

  get defaultFilter() {
    return [
      'all',
      'elements',
      'hidden_stems',
      'dynamics',
      'lz',
      'fj',
      'fm',
      'mental_patterns',
      'aspects',
      'festigend',
      'loesend',
      'weitere',
      'qi_phases',
      'stars',
      'i_ging',
      'na_yin',
      'bazi',
      'wiki',
    ];
  }

  get essentialFilter() {
    return ['dynamics', 'lz', 'fj', 'elements', 'bazi', 'wiki'];
  }

  isDefaultFilter(filter) {
    return JSON.stringify(filter) === JSON.stringify(this.defaultFilter);
  }

  saveFilter(currentFilter) {
    localStorage.setItem('systemsFilter', JSON.stringify(currentFilter));
  }

  loadFilter() {
    // On printing, there is no localStorage and the checkboxes are set by params
    if (this.onPrint) return;

    let filter;

    if (this.wrapperTarget.classList.contains('hidden')) {
      filter = this.essentialFilter;
    } else {
      const storedFilter = localStorage.getItem('systemsFilter');
      filter = storedFilter ? JSON.parse(storedFilter) : this.defaultFilter;
    }

    this.filterTargets.forEach(
      (target) =>
        (target.checked =
          !target.disabled && filter.includes(target.dataset.filter)),
    );
  }

  get aspectsController() {
    return this.application.getControllerForElementAndIdentifier(
      this.element,
      'aspects',
    );
  }

  get onPrint() {
    return document
      .getElementsByTagName('body')[0]
      .classList.contains('js-print');
  }
}
